import type {
	API_UpdatePlatformPlanRequest,
	PlatformPlanEntitlementDTO,
} from "@pilatestime-admin/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Archive, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	fetchPlatformPlanByIdQueryOptions,
	fetchPlatformPlanFeatureCodesQueryOptions,
	useArchivePlatformPlan,
	useUpdatePlatformPlan,
	useUpdatePlatformPlanEntitlements,
} from "@/api/platformPlans";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useApiParameters } from "@/hooks/useApiParameters";
import { EntitlementsEditor } from "./-components/EntitlementsEditor";
import { PlanForm } from "./-components/PlanForm";

export const Route = createFileRoute("/_auth/app/_app/platform-plans/$planId")({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		context.queryClient.ensureQueryData(
			fetchPlatformPlanByIdQueryOptions(token, params.planId),
		);
		context.queryClient.ensureQueryData(
			fetchPlatformPlanFeatureCodesQueryOptions(token),
		);
		return { userId, token, planId: params.planId };
	},
	component: PlatformPlanDetailPage,
});

function PlatformPlanDetailPage() {
	const { t } = useTranslation();
	const { token, planId } = Route.useLoaderData();
	const navigate = useNavigate();

	const { data: plan } = useSuspenseQuery(
		fetchPlatformPlanByIdQueryOptions(token, planId),
	);
	const { data: featureCodes } = useSuspenseQuery(
		fetchPlatformPlanFeatureCodesQueryOptions(token),
	);

	const updateMutation = useUpdatePlatformPlan();
	const archiveMutation = useArchivePlatformPlan();
	const entitlementsMutation = useUpdatePlatformPlanEntitlements();

	const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

	const handleSavePlan = (body: unknown) => {
		updateMutation.mutate({
			token,
			planId,
			body: body as API_UpdatePlatformPlanRequest,
		});
	};

	const handleSaveEntitlements = (
		entitlements: PlatformPlanEntitlementDTO[],
	) => {
		entitlementsMutation.mutate({ token, planId, body: { entitlements } });
	};

	const handleArchive = () => {
		archiveMutation.mutate(
			{ token, planId },
			{
				onSuccess: () => {
					setArchiveDialogOpen(false);
					navigate({ to: "/app/platform-plans" });
				},
			},
		);
	};

	const archiveDisabled = !plan.isActive || plan.subscriptionsCount > 0;

	return (
		<div className="container mx-auto p-6 space-y-6">
			<div className="flex items-center justify-between gap-3 flex-wrap">
				<div className="flex items-center gap-3">
					<Link
						to="/app/platform-plans"
						className="btn btn-ghost btn-sm"
						data-testid="platform-plans-detail-back"
					>
						<ArrowLeft className="h-4 w-4" />
						{t("common.back")}
					</Link>
					<div>
						<h1 className="text-2xl font-bold text-base-content">
							{plan.name}
						</h1>
						<p className="text-sm text-base-content/60 font-mono">
							{plan.slug}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<span
						className={`badge ${
							plan.isActive ? "badge-success" : "badge-ghost"
						}`}
					>
						{plan.isActive
							? t("platformPlans.status.active")
							: t("platformPlans.status.archived")}
					</span>
					{plan.isDefault && (
						<span className="badge badge-primary">
							{t("platformPlans.table.defaultBadge")}
						</span>
					)}
					<Button
						variant="outline"
						size="sm"
						onClick={() => setArchiveDialogOpen(true)}
						disabled={archiveDisabled}
						data-testid="platform-plan-archive-button"
					>
						<Archive className="h-4 w-4 mr-1" />
						{t("platformPlans.detail.archive")}
					</Button>
				</div>
			</div>

			{plan.subscriptionsCount > 0 && (
				<output className="alert alert-info block">
					<span>
						{t("platformPlans.detail.subscriptionsInfo", {
							count: plan.subscriptionsCount,
						})}
					</span>
				</output>
			)}

			<div className="card bg-base-100 shadow-sm">
				<div className="card-body">
					<h2 className="card-title text-lg mb-4">
						{t("platformPlans.detail.formTitle")}
					</h2>
					<PlanForm
						mode="edit"
						initial={plan}
						onSubmit={handleSavePlan}
						isSubmitting={updateMutation.isPending}
						onCancel={() => navigate({ to: "/app/platform-plans" })}
					/>
				</div>
			</div>

			<div className="card bg-base-100 shadow-sm">
				<div className="card-body">
					<h2 className="card-title text-lg mb-4">
						{t("platformPlans.entitlements.title")}
					</h2>
					<EntitlementsEditor
						featureCodes={featureCodes}
						initial={plan.entitlements}
						onSave={handleSaveEntitlements}
						isSaving={entitlementsMutation.isPending}
					/>
				</div>
			</div>

			<Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{t("platformPlans.detail.archiveDialog.title")}
						</DialogTitle>
						<DialogDescription>
							{t("platformPlans.detail.archiveDialog.description", {
								name: plan.name,
							})}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setArchiveDialogOpen(false)}
							disabled={archiveMutation.isPending}
						>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={handleArchive}
							disabled={archiveMutation.isPending}
						>
							{t("platformPlans.detail.archive")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
